import { BehaviorSubject } from 'rxjs';
import { createFiltered$ } from './utils';
import { LogoSection } from './generated';

export type ID = string | number;
export type Page = {
    id: ID;
    name: string;
    slug: string;
    type: PageType;
    pageComponents: PageComponentData[];
};
export enum HeroLayoutType {
    LeftTextFullMedia = 'left-text-full-media',
    RightTextFullMedia = 'right-text-full-media',
    MiddleTextFullMedia = 'middle-text-full-media',
    CustomTextLocationFullMedia = 'custom-text-location-full-media',
}

export const StoreStatusValuesReadonly = ['new', 'preview', 'review', 'published', 'closed', 'archived'] as const;

export enum ButtonActionType {
    NavigateTo = 'navigate-to',
    AddVariantToCart = 'add-variant-to-cart',
}

export enum ButtonPositionType {
    BelowText = 'below-text',
    Custom = 'custom',
}

export enum ButtonNavigationType {
    NavigateToProduct = 'navigate-to-product',
    NavigateToCollection = 'navigate-to-collection',
    NavigateToPage = 'navigate-to-page',
    NavigateToWebsite = 'navigate-to-website',
}

export enum NavigationTargetType {
    SitePage = 'site-page',
    SitePageSection = 'site-page-section',
    BuiltInSitePage = 'built-in-site-page',
    Custom = 'custom',
    Website = 'website',
}

export interface NavigationItem {
    label: string;
    targetType: NavigationTargetType;
    pageSlug?: string;
    pageSection?: string;
    builtInPage?: string;
    customSlug?: string;
    websiteUrl?: string;
    targetProductId?: ID | null;
    targetProductSlug?: string;
    targetCollectionId?: ID | null;
    targetCollectionSlug?: string;
}

export enum MarketingBannerType {
    LeftMediaRightText = 'left-media-right-text',
    LeftTextRightMedia = 'left-text-right-media',
    TopTextBelowMedia = 'top-text-below-media',
    MediaOnly = 'media-only',
    LeftMediaSlideWithBackground = 'left-media-slide-with-background',
    RightMediaSlideWithBackground = 'right-media-slide-with-background',
    TitledMedia = 'titled-media',
    TextOnly = 'text-only',
    DualText = 'dual-text',
    MiddleMediaSidesText = 'middle-media-sides-text',
}

export enum PageComponentTitleMode {
    Disabled = 'disabled',
    ForSection = 'for-section',
    ForComponent = 'for-component',
}

export enum PageComponentType {
    MarketingBanner = 'marketing-banner',
    KeyPointList = 'key-point-list',
    CardCarouselBanner = 'card-carousel-banner',
    HeroSection = 'hero-section',
    ExternalHtmlContent = 'external-html-content',
    FaqList = 'faq-list',
    FeaturedCollection = 'featured-collection',
    TopReviews = 'top-reviews',
    CollectionGroup = 'collection-group',
    EmailSubscription = 'email-subscription',
    ProductInfoSection = 'product-info-section',
    SimpleProductInfoSection = 'simple-product-info-section',
    ProductListingSection = 'product-listing-section',
    CollectionCarousel = 'collection-carousel',
    ProductReviews = 'product-reviews',
    ContactForm = 'contact-form',
    AboutForm = 'about-form',
    Unknown = '',
}

export enum SiteStyleType {
    CopyrightNotice = 'copyright-notice',
    Disclaimer = 'disclaimer',
    PDPAssetsArrowType = 'pdp-assets-arrow-type',
    MobileBuyBannerType = 'mobile-buy-banner-type',
}

export enum MobileMediaPosition {
    Top = 'top',
    Middle = 'middle',
    Bottom = 'bottom',
}

export enum EmailSubscriptionType {
    Raw = 'raw',
    Omnisend = 'omnisend',
}

export enum TextAlignment {
    Left = 'left',
    Center = 'center',
    Right = 'right',
}

export enum CollectionItemType {
    Product = 'Product',
    ProductVariant = 'ProductVariant',
}

export enum ProductCardType {
    Regular = 'regular',
    RegularType2 = 'regular-type2',
    HorizontalType = 'horizontal-type',
    LeftAssetSmall = 'left-asset-small',
    RightAssetSmall = 'right-asset-small',
    LeftAssetLarge = 'left-asset-large',
    RightAssetLarge = 'right-asset-large',
    TopAssetSmall = 'top-asset-small',
    TopAssetLarge = 'top-asset-large',
    FullAsset = 'full-asset',
}

export enum ProductCardShape {
    Rectangle = 'rectangle',
    Square = 'square',
}

export enum ProductListingType {
    All = 'all',
    Collection = 'collection',
    FacetValue = 'facetValue',
}

export enum PageType {
    General = 'general',
    Product = 'product',
    Collection = 'collection',
    Home = 'home',
    ProductSpecific = 'product-specific',
    CollectionSpecific = 'collection-specific',
    Unknown = '',
}

export enum LayoutComponentType {
    PageComponent = 'page-component',
    ProductListing = 'product-listing',
    ProductInfo = 'product-info',
    Unknown = '',
}

export type ProductSourceType = 'dynamic' | 'product' | 'variant';
export type ProductListingSourceType = 'dynamic' | 'all' | 'collection' | 'facetValue';

export type CustomizableAssetType = 'image' | 'text' | 'preview-container';

export type HeaderMessageDisplayOrderType = 'sequence' | 'random';
export type HeaderMessageSwitchType = 'auto' | 'manual';


export type Format = 'plain' | 'rich' | 'html';
export type LangMap = Record<string, string>;
export interface TextValue {
    format: Format;
    i18n: LangMap;
}

export enum OptionDsisplayType {
    ImageTiles = 'image-tiles',
    DropDownList = 'drop-down-list',
    OptionList = 'option-list',
}

export enum CarouselArrowType {
    LargeChevron = 'large-chevron',
    Chevron = 'chevron',
    Arrow = 'arrow',
    Caret = 'caret',
    None = 'none',
    SmallArrow = 'small-arrow',
}

export enum MobileBuyBannerType {
    Regular = 'regular',
    HideMSRP = 'hide-msrp',
}

export interface HeaderMessageConfig {
  displayOrder: HeaderMessageDisplayOrderType;
  switchType: HeaderMessageSwitchType;
  autoSwitchInterval: number;
  messageList: TextValue[];
}

// Define the interface for layout option data

export type NavigationMenuItemData = NavigationItem;

export type H1Source = 'auto' | 'custom' | 'none';
export type H1StyleMode = 'auto' | 'custom' | 'sr-only';

export interface WebPresenceConfigData {
    useBuiltInSeo: boolean;
    // SEO fields
    title: string;
    description: string;
    sitemapIndexing: boolean;
    // OpenGraph fields
    openGraphTitleSameAsSeoTitle: boolean;
    openGraphDescriptionSameAsSeoDescription: boolean;
    openGraphTitle: string;
    openGraphDescription: string;
    metaAssetId: ID | null;
    // H1 heading
    h1Source: H1Source;
    h1Text: TextValue;
    h1StyleMode: H1StyleMode;
    h1FontSize: number;
    h1MobileFontSize: number;
    h1Color: string;
    h1Alignment: string;
}

export interface LayoutOptionData {
    sectionName: string;
    layoutComponentType: LayoutComponentType;
    pageFullWidth?: boolean;
    sectionMaxHeight?: string;
    width: number;
    mobileWidth: number;
    topMargin: number;
    bottomMargin: number;
    mobileTopMargin: number;
    mobileBottomMargin: number;
    horizontalPadding: number;
    topPadding: number;
    bottomPadding: number;
    mobileHorizontalPadding: number;
    mobileTopPadding: number;
    mobileBottomPadding: number;
    sectionRoundedCorners?: string;
    mobileSectionRoundedCorners?: string;
}

export interface BannerButtonData {
    enabled: boolean;
    text: TextValue;
    textSize: number;
    mobileTextSize: number;
    textColor: string;
    backgroundColor: string;
    actionType: ButtonActionType;
    selectedVariantId: ID | null;
    navigationItem?: NavigationItem;
}

export interface HeroSectionData {
    layoutType: HeroLayoutType;
    mediaIds: ID[];
    mobileMediaIds: ID[];
    title: TextValue;
    subtitle: TextValue;
    titleFontSize: number;
    subtitleFontSize?: number;
    mobileTitleFontSize?: number;
    mobileSubtitleFontSize?: number;
    textColor: string;
    textPositionX?: number;
    textPositionY?: number;
    mobileTextPositionX?: number;
    mobileTextPositionY?: number;
    textWidth?: number;
    shadowTransparency: number;
    button: BannerButtonData | null;
    buttonPosition: ButtonPositionType;
    buttonAlignment?: TextAlignment;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonPositionMobile: ButtonPositionType;
    buttonAlignmentMobile?: TextAlignment;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
}

export interface ExternalHtmlContentData {
    htmlContent: TextValue;
    mediaIds: ID[];
    mobileMediaIds: ID[];
}

export interface KeyPointListData {
    titleFontSize?: number;
    subtitleFontSize?: number;
    textFontSize?: number;
    titleAlignment?: TextAlignment;
    textAlignment?: TextAlignment;
    iconSize?: number;
    backgroundColor: string;
    textColor: string;
    keyPointsLayoutType: string;
    mobileKeyPointsLayoutType?: string;
    keyPoints: KeySellingPointData[];
}

export interface KeySellingPointData {
    iconId: ID | null;
    title: TextValue;
    value: TextValue;
}

export interface CardCarouselBannerData {
    titleFontSize?: number;
    subtitleFontSize?: number;
    textFontSize?: number;
    titleAlignment?: TextAlignment;
    textAlignment?: TextAlignment;
    basicCardShape?: string;
    slideNumber?: number;
    mobileStyle?: string;
    backgroundColor?: string;
    textColor?: string;
    mediaRoundedCorners?: string;
    basicCards: BasicCardData[];
}

export interface BasicCardData {
    assetId: ID | null;
    title: TextValue;
    text: TextValue;
}

export interface QaData {
    question: TextValue;
    answer: TextValue;
}

export interface ProductListingData {
    productListingType: string;
    useInheritedCollection?: boolean;
    collectionId: ID | null;
    facetValueIds: ID[];
}

export interface MarketingBannerData {
    type: MarketingBannerType;
    mediaHeight?: number;
    mediaRoundedCorners?: string;
    mediaIds: ID[];
    mobileMediaIds: ID[];
    mobileMediaPosition: MobileMediaPosition;
    mobileMediaStyle: string;
    title: TextValue;
    text: TextValue;
    titleFontSize: number;
    textFontSize: number;
    textBarStyle: string;
    leftRightPartRatio: number;
    mediaTextGap: number;
    shadowTransparency: number;
    backgroundMediaId: ID | null;
    backgroundMobileMediaId: ID | null;
    autoSlideDelay: number;
    layoutStyle: string;
    backgroundColor: string;
    textColor: string;
    textAlignment: TextAlignment;
    textWidth: number;
    titleIndentation: number;
    textIndentation: number;
    secColTitle?: TextValue;
    secColText?: TextValue;
    secColTitleFontSize?: number;
    secColTextFontSize?: number;
    secColTextAlignment?: TextAlignment;
    secColTextWidth?: number;
    secColTitleIndentation?: number;
    secColTextIndentation?: number;
    button: BannerButtonData | null;
    buttonPosition: ButtonPositionType;
    buttonAlignment?: TextAlignment;
    buttonLocationX: number;
    buttonLocationY: number;
    buttonPositionMobile: ButtonPositionType;
    buttonAlignmentMobile?: TextAlignment;
    buttonLocationXMobile: number;
    buttonLocationYMobile: number;
}

export interface PageComponentData {
    type: PageComponentType;
    sectionId: string;
    titleMode?: PageComponentTitleMode;
    title?: TextValue;
    subtitle?: TextValue;
    titleUnderline?: boolean;
    layoutOption: LayoutOptionData | null;
    marketingBannerData?: MarketingBannerData | null;
    keyPointListData?: KeyPointListData | null;
    cardCarouselBannerData?: CardCarouselBannerData | null;
    heroSection?: HeroSectionData | null;
    externalHtmlContent?: ExternalHtmlContentData | null;
    faqList?: FaqListData | null;
    featuredCollection?: FeaturedCollectionData | null;
    topReviews?: TopReviewsData | null;
    collectionGroup?: CollectionGroupData | null;
    emailSubscription?: EmailSubscriptionData | null;
    productInfo?: ProductInfoData | null;
    simpleProductInfo?: SimpleProductInfoData | null;
    productListingInfo?: ProductListingData | null;
    productReviews?: ProductReviewsData | null;
    collectionCarouselData?: CollectionCarouselData | null;
    contactFormData?: ContactFormData | null;
    aboutFormData?: AboutFormData | null;
}

export interface ProductInfoData {
    sourceType?: ProductSourceType;
    productId: ID | null;
    productVariantId: ID | null;
    displayFeaturedReview?: boolean;
}

export interface SimpleProductInfoData {
    productVariantId: ID | null;
    /** Toggle flags */
    displayThumbnails: boolean;
    displayFeaturedReview: boolean;
    displaySku: boolean;
    displayProductFeatures: boolean;
    displayProductDescription: boolean;
    displayReviewInfo: boolean;
    displayProductSubtitle: boolean;
    displayPaymentCapabilities: boolean;
    displayProductOptions: boolean;
    displayAddToCart: boolean;
    displayProductKeyPoints: boolean;
    displayProductPageEntry: boolean;
}

export interface TopReviewsData {
    reviewIds: ID[];
}

export interface StoreSiteStyles {
    siteTitle?: string;
    displayHeaderMenu?: boolean;
    displayFooterMenu?: boolean;
    displayStoreName?: boolean;
    displayHeaderMessage?: boolean;
    copyrightNotice?: TextValue;
    disclaimer?: TextValue;
    pdpAssetsArrowType?: CarouselArrowType;
    mobileBuyBannerType?: MobileBuyBannerType;
}

export interface StoreSiteStylesInput extends StoreSiteStyles {}

export interface ProductVariantMetaData {
    enabled: boolean;
    type: string;
    content: string;
}

export interface AboutFormData {
    text: TextValue;
    citation: TextValue;
    featuredAssetId: ID | null;
}

export interface FaqListData {
    defaultDisplayCount: number;
    qaList: QaData[];
}

export interface ContactFormData {
    contactFormType: string;
    email: string;
    featuredAssetId: ID | null;
}

export interface CollectionCarouselData {
    textAlignment?: TextAlignment;
    mobileStyle: string;
    collectionIds: ID[];
}

export interface FeaturedCollectionData {
    collectionId: ID | null;
    displayType: CollectionItemType;
    productCardType: ProductCardType;
    cardsPerRow: number;
    mobileCardsPerRow?: number;
    productCardShape: ProductCardShape;
    displayFields?: string[];
    productCardRoundedCorners?: string;
    buttonText?: TextValue | null;
    buttonColorMode?: string;
    buttonBgColor?: string | null;
    buttonTextColor?: string | null;
}

export interface CollectionGroupData {
    groupName: TextValue;
    description: TextValue;
    backgroundAssetId: ID | null;
    featuredAssetIds: ID[];
    collectionIds: ID[];
}

export interface EmailSubscriptionData {
    type: EmailSubscriptionType;
    omnisendFormId: string;
    backgroundColor: string;
    backgroundImageId: ID | null;
    shadowTransparency: number;
}

export interface CollectionItemOrderData {
    collectionId: ID | null;
    enabled: boolean;
    orderType: CollectionItemType;
    variantIdsInOrder: ID[];
    productIdsInOrder: ID[];
}

export interface ProductReviewsData {
    backgroundColor: string;
    sourceType: Extract<ProductSourceType, 'dynamic' | 'product'>;
    productId: ID | null;
}

export interface BaseErpSettings {
    appId: string;
    appSecret: string;
}

export interface LingXingErpSettings extends BaseErpSettings {
    storeId: string;
    storeName: string;
}

export interface MockerErpSettings extends BaseErpSettings {
    storeId: string;
    storeName: string;
    logisticsSettings: ErpLingXingLogisticsSetting[];
}

export type ErpSettings = LingXingErpSettings | MockerErpSettings | BaseErpSettings;

export enum ErpTypes {
    LingXing = 'lingxing-erp',
}

export interface ErpIntegrationSettings {
    enabled: boolean;
    type: ErpTypes;
    platformSettings?: ErpSettings;
}

export interface ErpLingXingStoreSelection {
    storeId: string;
    storeName: string;
}

export interface ErpLingXingLogisticsSetting {
    erpLogisticsCarrierName: string;
    trackingNoSource: 'tracking_no' | 'waybill_no';
}

export const defaultLayoutOptionData: LayoutOptionData = {
    sectionName: '',
    layoutComponentType: LayoutComponentType.Unknown,
    pageFullWidth: false,
    sectionMaxHeight: 'none',
    width: 1,
    topMargin: 0,
    bottomMargin: 0,
    mobileWidth: 1,
    mobileTopMargin: 0,
    mobileBottomMargin: 0,
    horizontalPadding: 0,
    topPadding: 0,
    bottomPadding: 0,
    mobileHorizontalPadding: 0,
    mobileTopPadding: 0,
    mobileBottomPadding: 0,
    sectionRoundedCorners: 'none',
    mobileSectionRoundedCorners: 'none',
};

export const defaultNavigationItem: NavigationItem = {
    label: '',
    targetType: NavigationTargetType.SitePage,
    pageSlug: '',
    pageSection: '',
    builtInPage: '',
    customSlug: '',
    websiteUrl: '',
    targetProductId: null,
    targetProductSlug: '',
    targetCollectionId: null,
    targetCollectionSlug: '',
};

export const defaultPageComponentData: PageComponentData = {
    type: PageComponentType.ProductListingSection,
    sectionId: 'section-1',
    titleMode: PageComponentTitleMode.ForSection,
    title: { format: 'plain', i18n: { en: '' } },
    subtitle: { format: 'plain', i18n: { en: '' } },
    titleUnderline: true,
    layoutOption: defaultLayoutOptionData,
    marketingBannerData: undefined,
    keyPointListData: undefined,
    cardCarouselBannerData: undefined,
    heroSection: undefined,
    externalHtmlContent: undefined,
    faqList: undefined,
    featuredCollection: undefined,
    topReviews: undefined,
    collectionGroup: undefined,
    emailSubscription: undefined,
    productInfo: undefined,
    simpleProductInfo: undefined,
    productListingInfo: undefined,
    collectionCarouselData: undefined,
    contactFormData: undefined,
    aboutFormData: undefined,
    productReviews: undefined,
};

export const defaultBannerButtonData: BannerButtonData = {
    enabled: false,
    text: { format: 'plain', i18n: { en: '' } },
    textSize: 16,
    mobileTextSize: 14,
    textColor: '#000000',
    backgroundColor: '#ffffff',
    actionType: ButtonActionType.NavigateTo,
    selectedVariantId: null,
    navigationItem: { ...defaultNavigationItem },
};

// Default value for HeroSectionData
export const defaultHeroSectionData: HeroSectionData = {
    layoutType: HeroLayoutType.LeftTextFullMedia,
    mediaIds: [],
    mobileMediaIds: [],
    title: { format: 'rich', i18n: { en: '' } },
    subtitle: { format: 'rich', i18n: { en: '' } },
    textColor: '#000000',
    titleFontSize: 40,
    subtitleFontSize: 20,
    mobileTitleFontSize: 24,
    mobileSubtitleFontSize: 18,
    textPositionX: 0.10,
    textPositionY: 0.20,
    mobileTextPositionX: 0.10,
    mobileTextPositionY: 0.20,
    textWidth: 0.60,
    shadowTransparency: 1,
    button: { ...defaultBannerButtonData },
    buttonPosition: ButtonPositionType.BelowText,
    buttonAlignment: TextAlignment.Center,
    buttonLocationX: 0.5,
    buttonLocationY: 0.5,
    buttonPositionMobile: ButtonPositionType.BelowText,
    buttonAlignmentMobile: TextAlignment.Center,
    buttonLocationXMobile: 0.5,
    buttonLocationYMobile: 0.5,
};

// Default value for ExternalHtmlContentData
export const defaultExternalHtmlContentData: ExternalHtmlContentData = {
    htmlContent: { format: 'html', i18n: { en: '' } },
    mediaIds: [],
    mobileMediaIds: [],
};

export const defaultKeyPointListData: KeyPointListData = {
    iconSize: 60,
    titleFontSize: 32,
    subtitleFontSize: 24,
    textFontSize: 20,
    titleAlignment: TextAlignment.Center,
    textAlignment: TextAlignment.Center,
    backgroundColor: '#ffffff',
    textColor: '#000000',
    keyPointsLayoutType: 'horizontal',
    mobileKeyPointsLayoutType: 'horizontal',
    keyPoints: [],
};

export const defaultCardCarouselBannerData: CardCarouselBannerData = {
    titleFontSize: 32,
    subtitleFontSize: 24,
    textFontSize: 20,
    titleAlignment: TextAlignment.Center,
    textAlignment: TextAlignment.Center,
    basicCardShape: 'square',
    backgroundColor: '#ffffff',
    slideNumber: 4,
    mobileStyle: 'tiles',
    textColor: '#000000',
    mediaRoundedCorners: 'none',
    basicCards: [],
}

export const defaultAboutFormData: AboutFormData = {
    text: { format: 'rich', i18n: { en: '' } },
    citation: { format: 'rich', i18n: { en: '' } },
    featuredAssetId: null,
};

export const defaultFaqListData: FaqListData = {
    defaultDisplayCount: 3,
    qaList: [],
};

export const defaultContactFormData: ContactFormData = {
    contactFormType: '',
    email: '',
    featuredAssetId: null,
};

export const defaultCollectionCarouselData: CollectionCarouselData = {
    textAlignment: TextAlignment.Center,
    mobileStyle: 'tiles',
    collectionIds: [],
};

// Default value for CollectionGroupData
export const defaultCollectionGroupData: CollectionGroupData = {
    groupName: { format: 'rich', i18n: { en: '' } },
    description: { format: 'rich', i18n: { en: '' } },
    backgroundAssetId: null,
    featuredAssetIds: [],
    collectionIds: [],
};

// Default value for EmailSubscriptionData
export const defaultEmailSubscriptionData: EmailSubscriptionData = {
    type: EmailSubscriptionType.Raw,
    backgroundColor: '#ffffff',
    backgroundImageId: null,
    shadowTransparency: 1,
    omnisendFormId: '',
};

export const defaultFeaturedCollectionData: FeaturedCollectionData = {
    collectionId: null,
    displayType: CollectionItemType.Product,
    productCardType: ProductCardType.Regular,
    cardsPerRow: 4,
    mobileCardsPerRow: 2,
    productCardShape: ProductCardShape.Rectangle,
    displayFields: [],
    productCardRoundedCorners: 'none',
    buttonText: { format: 'plain', i18n: { en: 'Shop Now' } },
    buttonColorMode: 'default',
    buttonBgColor: null,
    buttonTextColor: null,
}

export const defaultProductListingData: ProductListingData = {
    productListingType: 'all',
    useInheritedCollection: false,
    collectionId: null,
    facetValueIds: [],
}

export const defaultSimpleProductInfoData: SimpleProductInfoData = {
    productVariantId: null,
    /** Toggle flags */
    displayThumbnails: false,
    displayFeaturedReview: false,
    displaySku: false,
    displayProductFeatures: false,
    displayProductDescription: false,
    displayReviewInfo: false,
    displayProductSubtitle: false,
    displayPaymentCapabilities: false,
    displayProductOptions: false,
    displayAddToCart: false,
    displayProductKeyPoints: false,
    displayProductPageEntry: false,
}

export const defaultProductInfoData: ProductInfoData = {
    sourceType: 'variant',
    productId: null,
    productVariantId: null,
    displayFeaturedReview: false,
};

export const defaultTopReviewsData: TopReviewsData = {
    reviewIds: [],
};

// Default value for MarketingBannerData
export const defaultMarketingBannerData: MarketingBannerData = {
    type: MarketingBannerType.LeftMediaRightText,
    mediaHeight: 400,
    mediaRoundedCorners: 'none',
    mediaIds: [],
    mobileMediaIds: [],
    mobileMediaPosition: MobileMediaPosition.Top,
    mobileMediaStyle: 'slide',
    title: { format: 'rich', i18n: { en: '' } },
    text: { format: 'rich', i18n: { en: '' } },
    titleFontSize: 32,
    textFontSize: 16,
    textBarStyle: 'none',
    leftRightPartRatio: 0.5,
    mediaTextGap: 0,
    shadowTransparency: 1,
    backgroundMediaId: null,
    backgroundMobileMediaId: null,
    backgroundColor: '#ffffff',
    autoSlideDelay: 3.0,
    layoutStyle: 'normal',
    textColor: '#000000',
    textAlignment: TextAlignment.Center,
    textWidth: 1,
    titleIndentation: 0,
    textIndentation: 0,
    secColTitle: { format: 'rich', i18n: { en: '' } },
    secColText: { format: 'rich', i18n: { en: '' } },
    secColTitleFontSize: 32,
    secColTextFontSize: 16,
    secColTextAlignment: TextAlignment.Center,
    secColTextWidth: 1,
    secColTitleIndentation: 0,
    secColTextIndentation: 0,
    button: { ...defaultBannerButtonData },
    buttonPosition: ButtonPositionType.BelowText,
    buttonAlignment: TextAlignment.Center,
    buttonLocationX: 0.5,
    buttonLocationY: 0.5,
    buttonPositionMobile: ButtonPositionType.BelowText,
    buttonAlignmentMobile: TextAlignment.Center,
    buttonLocationXMobile: 0.5,
    buttonLocationYMobile: 0.5,
};

export const defaultProductReviewsData: ProductReviewsData = {
    backgroundColor: '#ffffff',
    sourceType: 'dynamic',
    productId: null,
}

export const CustomPermissions = {
    ReadCustomDomain: 'ReadCustomDomain',
    CreateCustomDomain: 'CreateCustomDomain',
    UpdateCustomDomain: 'UpdateCustomDomain',
    DeleteCustomDomain: 'DeleteCustomDomain',
    ReadSite: 'ReadSite',
    UpdateSite: 'UpdateSite',
    DeleteSite: 'DeleteSite',
    CreateSite: 'CreateSite',
    ReadSiteVersion: 'ReadSiteVersion',
    CreateSiteVersion: 'CreateSiteVersion',
    UpdateSiteVersion: 'UpdateSiteVersion',
    DeleteSiteVersion: 'DeleteSiteVersion',
}

export const DEFAULT_LOGO_SECTION: LogoSection = {
  normalLogoId: null,
  smallLogoId: null,
  nameLogoId: null,
  footerLogoId: null,
  browserIconId: null,
  webclipIconId: null,
};

export const DEFAULT_COLOR_CONFIG: ColorConfig = {
  mainColor: '#ffffff',
  mainTextColor: '#000000',
  secondaryColor: '#cccccc',
  secondaryTextColor: '#333333',
  headerBackgroundColor: '#ffffff',
  headerFontColor: '#000000',
  footerBackgroundColor: '#ffffff',
  footerFontColor: '#000000',
};

export const DEFAULT_SNS: SnsInfo = {
  instagram: '',
  facebook: '',
  xTwitter: '',
  youtube: '',
  tiktok: '',
};

export const DEFAULT_FOOTER_NAVIGATION_COLUMN: FooterNavigationColumn = {
  name: '',
  items: [],
};

export const DEFAULT_POLICY_CONTENT_DATA: PolicyContentData = {
  type: '',
  title: '',
  content: '',
  path: '',
};

export interface PolicyTemplate {
    type: string;
    name: string;
    defaultContent: string;
    required: boolean;
    slug?: string;
}

export interface PolicyContentData {
    type: string;
    title: string;
    path: string;
    content: string;
}

export type ColorConfig = {
  mainColor: string;
  mainTextColor: string;
  secondaryColor: string;
  secondaryTextColor: string;
  headerBackgroundColor: string;
  headerFontColor: string;
  footerBackgroundColor: string;
  footerFontColor: string;
};

export type SnsInfo = {
  instagram: string;
  facebook: string;
  xTwitter: string;
  youtube: string;
  tiktok?: string;
};

export type FooterNavigationColumn = {
    name: string;
    items: FooterNavigationItem[];
};

export type FooterNavigationItem = {
    targetType?: string;
    label?: string;
    pageSlug?: string;
    pageSection?: string;
    builtInPage?: string;
    customSlug?: string;
    websiteUrl?: string;
    targetProductId?: ID | null;
    targetProductSlug?: string;
    targetCollectionId?: ID | null;
    targetCollectionSlug?: string;
};

export interface NavContext {
    enableCurrentPage: boolean;
    pages$: BehaviorSubject<Page[]>;
    currentSlug$: BehaviorSubject<string | null>;
    currentPageComponents$: BehaviorSubject<Array<PageComponentData | string>>;
    filteredPages$: ReturnType<typeof createFiltered$>;
    policyContentList$: BehaviorSubject<PolicyContentData[]>;
}
